import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Sparkles, Heart, ChevronLeft, ArrowUpDown, Home } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Prenoms() {
  const navigate = useNavigate();
  const [prenoms, setPrenoms] = useState([]);
  const [filteredPrenoms, setFilteredPrenoms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("tous");
  const [sortBy, setSortBy] = useState("popularite");
  const [loading, setLoading] = useState(true);
  const [displayLimit, setDisplayLimit] = useState(20);

  useEffect(() => {
    fetchPrenoms();
  }, []);

  useEffect(() => {
    filterAndSortPrenoms();
  }, [searchTerm, selectedGenre, sortBy, prenoms]);

  const fetchPrenoms = async () => {
    try {
      const response = await fetch(`${API}/prenoms`);
      if (response.ok) {
        const data = await response.json();
        setPrenoms(data);
        setFilteredPrenoms(data);
      } else {
        const fallbackPrenoms = generateFallbackPrenoms();
        setPrenoms(fallbackPrenoms);
        setFilteredPrenoms(fallbackPrenoms);
      }
    } catch (error) {
      console.error("Error fetching prenoms:", error);
      const fallbackPrenoms = generateFallbackPrenoms();
      setPrenoms(fallbackPrenoms);
      setFilteredPrenoms(fallbackPrenoms);
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackPrenoms = () => {
    return [
      { prenom: "Kouassi", genre: "masculin", signification: "Né un dimanche", origine: "Jour de naissance", popularite: 100, variantes: ["Kwasi", "Kouassy"] },
      { prenom: "Akissi", genre: "feminin", signification: "Née un dimanche", origine: "Jour de naissance", popularite: 96, variantes: ["Akisi", "Akessi"] },
      { prenom: "Kouadio", genre: "masculin", signification: "Né un lundi", origine: "Jour de naissance", popularite: 94, variantes: ["Kwadio"] },
      { prenom: "Adjoua", genre: "feminin", signification: "Née un lundi", origine: "Jour de naissance", popularite: 92, variantes: ["Ajua"] },
      { prenom: "N'Goran", genre: "masculin", signification: "Né un mardi", origine: "Jour de naissance", popularite: 88, variantes: ["Ngoran"] },
      { prenom: "Amenan", genre: "feminin", signification: "Née un mardi", origine: "Jour de naissance", popularite: 86, variantes: ["Amanan"] },
      { prenom: "Konan", genre: "masculin", signification: "Né un mercredi", origine: "Jour de naissance", popularite: 95, variantes: ["Konin"] },
      { prenom: "Ahou", genre: "feminin", signification: "Née un mercredi", origine: "Jour de naissance", popularite: 87, variantes: ["Ahui"] },
      { prenom: "Yao", genre: "masculin", signification: "Né un jeudi", origine: "Jour de naissance", popularite: 92, variantes: ["Yawo"] },
      { prenom: "Aya", genre: "feminin", signification: "Née un jeudi", origine: "Jour de naissance", popularite: 92, variantes: ["Ayawa"] },
      { prenom: "Koffi", genre: "masculin", signification: "Né un vendredi", origine: "Jour de naissance", popularite: 90, variantes: ["Kofi"] },
      { prenom: "Affoué", genre: "feminin", signification: "Née un vendredi", origine: "Jour de naissance", popularite: 85, variantes: ["Afoue"] },
      { prenom: "Kouamé", genre: "masculin", signification: "Né un samedi", origine: "Jour de naissance", popularite: 98, variantes: ["Kwame"] },
      { prenom: "Amoin", genre: "feminin", signification: "Née un samedi", origine: "Jour de naissance", popularite: 90, variantes: ["Amwoin"] },
    ];
  };

  const filterAndSortPrenoms = () => {
    let filtered = prenoms;

    if (selectedGenre !== "tous") {
      filtered = filtered.filter(p => p.genre === selectedGenre);
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.signification.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.origine && p.origine.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "popularite":
          return (b.popularite || 0) - (a.popularite || 0);
        case "alphabetique":
          return a.prenom.localeCompare(b.prenom);
        case "genre":
          return a.genre.localeCompare(b.genre);
        default:
          return 0;
      }
    });

    setFilteredPrenoms(filtered);
  };

  const getGenreIcon = (genre) => {
    return genre === "masculin" ? "👨" : "👩";
  };

  const getGenreColor = (genre) => {
    return genre === "masculin" 
      ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" 
      : "bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20";
  };

  const loadMore = () => {
    setDisplayLimit(prev => prev + 20);
  };

  const displayedPrenoms = filteredPrenoms.slice(0, displayLimit);

  return (
    <div className="min-h-screen bg-background">
      {/* Simple Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-5 h-5" />
            <span className="hidden sm:inline">Accueil</span>
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h1 className="font-heading text-xl font-bold">Prénoms Baoulé</h1>
          </div>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </div>
      
      <div className="pt-8">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-primary/20 via-primary/10 to-background py-12 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour à l'accueil
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-4xl md:text-5xl font-bold">
                  Prénoms Baoulé
                </h2>
                <p className="text-muted-foreground text-lg">
                  Découvrez tous les prénoms traditionnels baoulé
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher un prénom ou une signification..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-14 text-base shadow-sm"
              />
            </div>

            {/* Filters Row */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              {/* Genre Tabs */}
              <Tabs value={selectedGenre} onValueChange={setSelectedGenre} className="flex-1">
                <TabsList className="grid w-full grid-cols-3 h-12">
                  <TabsTrigger value="tous" className="text-sm">
                    <Users className="w-4 h-4 mr-2" />
                    Tous
                  </TabsTrigger>
                  <TabsTrigger value="masculin" className="text-sm">
                    👨 Garçons
                  </TabsTrigger>
                  <TabsTrigger value="feminin" className="text-sm">
                    👩 Filles
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Sort Select */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[200px] h-12">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popularite">Plus populaires</SelectItem>
                  <SelectItem value="alphabetique">Alphabétique</SelectItem>
                  <SelectItem value="genre">Par genre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="text-center p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <div className="text-3xl font-bold text-primary">{prenoms.length}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </Card>
            <Card className="text-center p-4 bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {prenoms.filter(p => p.genre === "masculin").length}
              </div>
              <div className="text-sm text-muted-foreground">👨 Garçons</div>
            </Card>
            <Card className="text-center p-4 bg-gradient-to-br from-pink-500/10 to-pink-500/5 border-pink-500/20">
              <div className="text-3xl font-bold text-pink-600 dark:text-pink-400">
                {prenoms.filter(p => p.genre === "feminin").length}
              </div>
              <div className="text-sm text-muted-foreground">👩 Filles</div>
            </Card>
            <Card className="text-center p-4 bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {filteredPrenoms.length}
              </div>
              <div className="text-sm text-muted-foreground">Résultats</div>
            </Card>
          </div>

          {/* Prenoms Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array(12).fill(0).map((_, i) => (
                <Card key={i} className="p-4 animate-pulse">
                  <div className="h-6 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </Card>
              ))}
            </div>
          ) : filteredPrenoms.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
                {displayedPrenoms.map((prenom, index) => (
                  <Card 
                    key={prenom.prenom_id || index}
                    className="group hover:shadow-xl hover:scale-105 transition-all duration-300 border-border/50 overflow-hidden"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-heading text-xl font-bold group-hover:text-primary transition-colors mb-1">
                            {prenom.prenom}
                          </h3>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getGenreColor(prenom.genre)}`}>
                            {getGenreIcon(prenom.genre)} {prenom.genre}
                          </span>
                        </div>
                        {prenom.popularite >= 90 && (
                          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10">
                            <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500" />
                            <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                              {prenom.popularite}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2">
                        {prenom.signification}
                      </p>
                      
                      {prenom.origine && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                          <span className="text-primary">📚</span>
                          <span>{prenom.origine}</span>
                        </div>
                      )}
                      
                      {prenom.variantes && prenom.variantes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <div className="text-xs text-muted-foreground">
                            <span className="font-semibold">Variantes : </span>
                            {prenom.variantes.join(", ")}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Load More */}
              {displayLimit < filteredPrenoms.length && (
                <div className="text-center mt-8">
                  <button 
                    onClick={loadMore}
                    className="px-8 py-3 rounded-full bg-primary/10 text-primary font-semibold hover:bg-primary/20 transition-colors"
                  >
                    Voir plus de prénoms ({filteredPrenoms.length - displayLimit} restants)
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="text-7xl mb-4">🔍</div>
              <h3 className="font-heading text-2xl font-bold mb-2">Aucun prénom trouvé</h3>
              <p className="text-muted-foreground mb-6">
                Essayez de modifier votre recherche ou vos filtres
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedGenre("tous");
                  setSortBy("popularite");
                }}
                className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}

          {/* Info Card */}
          <Card className="mt-12 mb-8 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-bold mb-3">
                    La Tradition des Prénoms Baoulé
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Dans la culture baoulé, les prénoms sont traditionnellement donnés selon le jour de naissance. 
                    Chaque jour de la semaine a son prénom masculin et féminin correspondant. Cette tradition 
                    ancestrale permet de connaître immédiatement le jour de naissance d'une personne simplement 
                    en entendant son prénom !
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs">📅</span>
                      </div>
                      <div className="text-sm">
                        <div className="font-semibold mb-1">Jour de naissance</div>
                        <div className="text-muted-foreground">La majorité des prénoms correspondent au jour de la semaine</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs">👨‍👩‍👧‍👦</span>
                      </div>
                      <div className="text-sm">
                        <div className="font-semibold mb-1">Rang de naissance</div>
                        <div className="text-muted-foreground">Certains prénoms indiquent le rang dans la fratrie</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Simple Footer */}
      <div className="border-t bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            © 2024 Mon Baoulé - Découvrez la richesse de la culture baoulé
          </p>
        </div>
      </div>
    </div>
  );
}
